import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ProjectService} from "../project/project.service";
import {SubstituteUserService} from "./substitute-user.service";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {Subscription} from "rxjs";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SelectableUserModel} from "../types/selectable-user.model";
import {SearchHelpers} from "../helpers/search-helper";
import { PrivilegeViewService } from "../privilege-view/privilege-view.service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "substitute-user",
  templateUrl: "./substitute-user.template.pug",
  providers: [
    SubstituteUserService,
    ProjectService,
  ],
})
export class SubstituteUserComponent implements OnInit, OnDestroy {
  error: Error;
  message: string;
  readyToAssignUsers: boolean = false; // If a user and a project are selected
  // Users
  users: any[] = [];
  filteredUsers: SelectableUserModel[] = [];
  displayedUsers: SelectableUserModel[] = [];
  selectedUsers: SelectableUserModel[] = [];
  currentPageUsers: number = DEFAULT_PAGE;
  pageSizeUsers: number = DEFAULT_PAGE_LIMIT;
  maxPagesUsers: number;
  searchTextUsers: string = "";
  private userSubscription: Subscription;
  // Projects
  projects: any[] = [];
  filteredProjects: any[] = [];
  displayedProjects: any[] = [];
  selectedProjects: any[] = [];
  currentPageProjects: number = DEFAULT_PAGE;
  pageSizeProjects: number = DEFAULT_PAGE_LIMIT;
  maxPagesProjects: number;
  searchTextProjects: string = "";
  private projectSubscription: Subscription;
  showFedLlcStatus: boolean = false;
  loading: boolean = false;

  @Input()
  disableAll: boolean = false;

  // TODO we're really at the point where a class managing client side searching would help
  private recalculateMaxPagesUsers() {
    this.maxPagesUsers = Math.ceil(this.filteredUsers.length / (+this.pageSizeUsers));
  }

  private recalculateMaxPagesProjects() {
    this.maxPagesProjects = Math.ceil(this.filteredProjects.length / (+this.pageSizeProjects));
  }

  private updateSelectedUsers(): void {
    this.selectedUsers = this.users.filter(it => it.selected);
  }

  private updateSelectedProjects(): void {
    this.selectedProjects = this.projects.filter(it => it.selected);
  }

  private updateDisplayUsers() {
    const start = (this.currentPageUsers - 1) * this.pageSizeUsers;
    const end = start + this.pageSizeUsers;

    this.filteredUsers = this.users.filter((user) => {
      if (this.searchTextUsers) {
        const values = [
          user.id,
          user.firstName,
          user.lastName,
          user.emailAddress,
          user.roles,
          ...user.privileges,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchTextUsers) // split on spaces
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedUsers = this.filteredUsers.slice(start, end);

    this.updateSelectedUsers();
    this.recalculateMaxPagesUsers();
  }

  private updateDisplayProjects() {
    const start = (this.currentPageProjects - 1) * this.pageSizeProjects;
    const end = start + this.pageSizeProjects;

    this.filteredProjects = this.projects.filter((project) => {
      if (this.searchTextProjects) {
        const values = [
          project.id,
          project.projectName,
          project.country,
          project.geoRegion,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchTextProjects) // split on spaces
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedProjects = this.filteredProjects.slice(start, end);

    this.updateSelectedProjects();
    this.recalculateMaxPagesProjects();
  }

  onListControlsChangeUsers(event) {
    switch (event["key"]) {
      case "page":
        this.currentPageUsers = event["value"];
        break;
      case "search":
        this.currentPageUsers = DEFAULT_PAGE;
        this.searchTextUsers = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.updateDisplayUsers();
  }

  onListControlsChangeProjects(event) {
    switch (event["key"]) {
      case "page":
        this.currentPageProjects = event["value"];
        break;
      case "search":
        this.currentPageProjects = DEFAULT_PAGE;
        this.searchTextProjects = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.updateDisplayProjects();
  }

  updateReadyToAssignUsers() {
    this.updateSelectedUsers();
    this.updateSelectedProjects();
    this.readyToAssignUsers = !!(this.selectedUsers.length && this.selectedProjects.length);
    
  }

  deselectAllVisibleUsers(): void {
    for (const user of this.filteredUsers) {
      user.selected = false;
    }
    this.updateSelectedUsers();
    this.updateReadyToAssignUsers();
  }

  deselectAllVisibleProjects(): void {
    for (const project of this.filteredProjects) {
      project.selected = false;
    }
    this.updateSelectedProjects();
    this.updateReadyToAssignUsers();
  }

  deselectAllUsers(): void {
    for (const user of this.users) {
      user.selected = false;
    }
    this.updateSelectedUsers();
    this.updateReadyToAssignUsers();
  }

  deselectAllProjects(): void {
    for (const project of this.projects) {
      project.selected = false;
    }
    this.updateSelectedProjects();
    this.updateReadyToAssignUsers();
  }

  selectAllVisibleUsers(): void {
    for (const user of this.filteredUsers) {
      user.selected = true;
    }
    this.updateSelectedUsers();
    this.updateReadyToAssignUsers();
  }

  selectAllVisibleProjects(): void {
    for (const project of this.filteredProjects) {
      project.selected = true;
    }
    this.updateSelectedProjects();
    this.updateReadyToAssignUsers();
  }

  selectAllUsers(): void {
    for (const user of this.users) {
      user.selected = true;
    }
    this.updateSelectedUsers();
    this.updateReadyToAssignUsers();
  }

  selectAllProjects(): void {
    for (const project of this.projects) {
      project.selected = true;
    }
    this.updateSelectedProjects();
    this.updateReadyToAssignUsers();
  }

  constructor(
    private substituteUserService: SubstituteUserService,
    public route: ActivatedRoute,
    public router: Router,
    private appSettingsService: AppSettingsService,
    private projectService: ProjectService,
    private privilegesService: PrivilegeViewService,
  ) {
  }

  @handleErrors()
  async loadData(): Promise<void> {
    this.error = undefined;
    this.loading = true;
    let domain = "";
    if (this.privilegesService.POR31302flag) {
      domain = this.privilegesService.getDomainView();
    }
    const {projects, users} = await this.projectService.getAssignableSubstituteUsers(domain).toPromise();

    this.projects = projects;
    this.users = users;

    this.recalculateMaxPagesUsers();
    this.recalculateMaxPagesProjects();
    this.updateDisplayUsers();
    this.updateDisplayProjects();
    this.updateReadyToAssignUsers();
    this.showFedLlcStatus = this.privilegesService.getDomainView() === "fedLLC";
    this.loading = false;
  }

  @handleErrors()
  async ngOnInit() {
    await this.loadData();
  }

  ngOnDestroy() {
    if (this.userSubscription && !this.userSubscription.closed) {
      this.userSubscription.unsubscribe();
    }
  }

  public navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["substitute-user"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async assignUsers(): Promise<void> {
    this.error = undefined;
    this.loading = true;
    this.message = undefined;
    const projectIds = this.selectedProjects.map(it => it.id);
    const userIds = this.selectedUsers.map(it => it.id);
    const response = await this.projectService.crossProductUserProjectAssignment(projectIds, userIds).toPromise();
    if (response && response.message) this.message = response.message;
    await this.loadData();
    this.loading = false;
  }
}
